import { createApp } from "vue";
import { Dropdown } from "bootstrap";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import App from "./App.vue";
import router from "./router";
import BootstrapVueNext from "bootstrap-vue-next";
import VueApexCharts from "vue3-apexcharts";
import { QuillEditor } from '@vueup/vue-quill'
import Vue3Prism from 'vue3-prism/lib/Vue3Prism.common.js'

import { globalHelper } from './utils/helpers'


import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import ToastComponent from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import Vue3EasyDataTable from 'vue3-easy-data-table';

import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

// Import specific icons
import { faUserSecret } from '@fortawesome/free-solid-svg-icons'
import { faFacebook } from '@fortawesome/free-brands-svg-icons'
import VueGoodTablePlugin from 'vue-good-table-next';
import Swal from 'sweetalert2'

import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue-next/dist/bootstrap-vue-next.css";
import "swiper/css";
import "swiper/css/bundle";
import 'flatpickr/dist/flatpickr.css';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '@vueup/vue-quill/dist/vue-quill.bubble.css';
import "vue3-prism/lib/Vue3Prism.css"
import 'vue3-easy-data-table/dist/style.css';
import 'vue-good-table-next/dist/vue-good-table-next.css';
import 'sweetalert2/src/sweetalert2.scss'

import 'vue-select/dist/vue-select.css';


import "./assets/custom.scss";
import { useAuthStore } from "./stores/auth";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import 'sweetalert2/dist/sweetalert2.min.css'
import PaginatorContent from "@/components/PaginatorContent.vue";

import vSelect from 'vue-select'
import stateStore from "./utils/store";


// Add icons to the library
library.add(faUserSecret, faFacebook)


const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App)
app.use(pinia);

// Staff session handoff: if the URL carries a one-time ?staff_token=..., the
// token itself is the whole session (no localStorage handoff), so this works
// even when the link is opened in a different browser or on another device.
// Set the token, then fetch the staff's user/permissions from the API. This
// runs before the router installs its guards/initial navigation, so by the
// time the guard checks auth.token it's already populated.
(() => {
  const params = new URLSearchParams(window.location.search);
  const staffToken = params.get("staff_token");
  if (!staffToken) return;

  params.delete("staff_token");
  const cleanSearch = params.toString();
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + (cleanSearch ? `?${cleanSearch}` : "") + window.location.hash
  );

  const auth = useAuthStore();
  auth.setToken(staffToken);
  // Placeholder so fetchPermissions() (which early-returns without a user)
  // proceeds; it replaces this with the real user from the API response.
  auth.setUser({});
  auth.fetchPermissions();
})();

// Admin-to-customer session handoff: the admin app opens
// /staff-session?data=<base64(encodeURIComponent(JSON))> with the payload
// { token, user, permissions }. There is no router entry for /staff-session —
// this decodes the payload, seeds the auth store, then rewrites the URL to
// "/" before the router's first navigation resolves, so it never 404s.
(() => {
  if (window.location.pathname !== "/staff-session") return;

  const params = new URLSearchParams(window.location.search);
  const data = params.get("data");
  window.history.replaceState({}, document.title, "/");
  if (!data) return;

  try {
    const payload = JSON.parse(decodeURIComponent(atob(data)));
    const auth = useAuthStore();
    auth.setToken(payload.token);
    auth.setUser(payload.user ?? {});
    auth.setPermissions(payload.permissions ?? {});
    auth.fetchPermissions();
  } catch (e) {
    console.error("Invalid staff session payload:", e);
  }
})();

app.use(router);
app.use(VueApexCharts);
app.use(BootstrapVueNext);
app.use(PrimeVue);
app.use(ToastService);

app.use(Toast);
app.use(VueGoodTablePlugin);




router.beforeEach((to, from, next) => {
  const auth = useAuthStore();

  // Not logged in → login page
  if (to.meta.requiresAuth && !auth.token) {
    localStorage.clear();
    return next({ name: "LoginPage" });
  }

  const isStaff = !!auth.user?.is_staff || !!auth.user?.customer_id;

  if (isStaff) {
    // Staff: gate by staffCodes meta (if the route declares any)
    const codes = to.meta.staffCodes as string[] | undefined;
    if (codes && codes.length > 0) {
      const allowed = codes.some((code) => auth.staffCodes.includes(code));
      if (!allowed) return next({ name: "ErrorPage" });
    }
  } else {
    // Admin: gate by permissions meta (object key === 1)
    const perms = to.meta.permissions as string[] | undefined;
    if (perms && perms.length > 0) {
      const allowed = perms.every((p) => auth.permissions[p] === 1);
      if (!allowed) return next({ name: "ErrorPage" });
    }
  }

  next();
})


let _lastPermsFetch = 0;
router.afterEach(() => {
  const stateStoreInstance = stateStore;
  if (stateStoreInstance.open) {
    stateStoreInstance.onChange();
  }

  // Refresh permissions on every navigation (throttled to once per 10s)
  const auth = useAuthStore();
  if (auth.token) {
    const now = Date.now();
    if (now - _lastPermsFetch > 10_000) {
      _lastPermsFetch = now;
      auth.fetchPermissions();
    }
  }
});

// Register Toast globally
app.component('Toast', ToastComponent);
app.component('QuillEditor', QuillEditor)
.use(Vue3Prism)

app.component('EasyDataTable', Vue3EasyDataTable);
app.component('font-awesome-icon', FontAwesomeIcon)
app.component("PaginatorContent",   PaginatorContent);

app.component('v-select', vSelect)
// Add global function
app.config.globalProperties.$showSuccessToast = (message: string) => {
  const toast = useToast();
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: message,
    life: 3000,
  });
};

app.config.globalProperties.$showErrorToast = (message: string) => {
  const toast = useToast();
  toast.add({
    severity: 'error',
    summary: 'Erreur',
    detail: message,
    life: 3000,
  });
};

app.config.globalProperties.$globalHelper = globalHelper

app.mount("#app");

// Global: initialize all Bootstrap dropdowns with Popper strategy:'fixed'
// so they render above any overflow:auto/scroll container (tables, vgt-responsive…)
const initFixedDropdowns = () => {
  document.querySelectorAll<HTMLElement>('[data-bs-toggle="dropdown"]').forEach((el) => {
    if (Dropdown.getInstance(el)) return;
    new Dropdown(el, {
      popperConfig: (defaultConfig: any) => ({ ...defaultConfig, strategy: "fixed" }),
    });
  });
};
initFixedDropdowns();
let _dropdownDebounce: ReturnType<typeof setTimeout>;
new MutationObserver(() => {
  clearTimeout(_dropdownDebounce);
  _dropdownDebounce = setTimeout(initFixedDropdowns, 80);
}).observe(document.body, { childList: true, subtree: true });

// 🔥 Global permissions refresher
const auth = useAuthStore()

// fetch once on startup
auth.fetchPermissions()

// refresh every 60s
setInterval(() => {
  auth.fetchPermissions()
}, 60000)
